import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  CalendarIcon,
  UserIcon,
  FlagIcon,
  ClockIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface TaskFormData {
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  status: 'New Client' | 'Active Client' | 'Target' | 'Placed' | 'Follow-up';
  type: 'Initial Contact' | 'Assessment' | 'Facility Visit' | 'Documentation' | 'Insurance Verification' | 'Contract Review' | 'Move Coordination' | 'Follow-up' | 'Other';
  dueDate: string;
  clientId?: string;
  assignedTo: string;
  checklist: Array<{
    item: string;
    completed: boolean;
  }>;
  estimatedHours?: number;
  tags?: string[];
}

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const TaskForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [checklistItems, setChecklistItems] = useState<Array<{ item: string; completed: boolean }>>([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');

  const { success: showSuccess, error: showError } = useToast();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<TaskFormData>({
    defaultValues: {
      priority: 'Medium',
      status: 'New Client',
      type: 'Other',
      checklist: []
    }
  });

  // Watch for changes
  const watchedType = watch('type');

  // Fetch clients and users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, usersRes] = await Promise.all([
          fetch('/api/clients', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch('/api/auth/users', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);

        if (clientsRes.ok) {
          const clientsData = await clientsRes.json();
          setClients(clientsData.data.clients || []);
        }

        if (usersRes.ok) {
          const usersData = await usersRes.json();
          console.log('Users data received:', usersData);
          setUsers(usersData.data.users || []);
        } else {
          console.error('Users fetch failed:', usersRes.status, usersRes.statusText);
          const errorText = await usersRes.text();
          console.error('Users fetch error response:', errorText);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Fetch task data if editing
  useEffect(() => {
    if (isEditing && id) {
      const fetchTask = async () => {
        try {
          setLoading(true);
          const response = await fetch(`/api/tasks/${id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch task');
          }

          const data = await response.json();
          const task = data.data.task;

          // Set form values
          setValue('title', task.title);
          setValue('description', task.description || '');
          setValue('priority', task.priority);
          setValue('status', task.status);
          setValue('type', task.type);
          setValue('dueDate', task.dueDate?.split('T')[0] || '');
          setValue('clientId', task.clientId?._id || '');
          setValue('assignedTo', task.assignedTo._id);
          setValue('estimatedHours', task.estimatedHours || '');

          // Set checklist
          if (task.checklist) {
            setChecklistItems(task.checklist);
            setValue('checklist', task.checklist);
          }
        } catch (error) {
          console.error('Error fetching task:', error);
          showError('Error', 'Failed to fetch task details');
          navigate('/tasks');
        } finally {
          setLoading(false);
        }
      };

      fetchTask();
    }
  }, [isEditing, id, setValue, showError, navigate]);

  // Handle form submission
  const onSubmit = async (data: TaskFormData) => {
    try {
      const taskData = {
        ...data,
        checklist: checklistItems,
        clientId: data.clientId || undefined
      };

      const url = isEditing ? `/api/tasks/${id}` : '/api/tasks';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(taskData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save task');
      }

      showSuccess(
        'Success',
        `Task ${isEditing ? 'updated' : 'created'} successfully`
      );
      navigate('/tasks');
    } catch (error) {
      console.error('Error saving task:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to save task');
    }
  };

  // Add checklist item
  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      const newItem = { item: newChecklistItem.trim(), completed: false };
      const updatedItems = [...checklistItems, newItem];
      setChecklistItems(updatedItems);
      setValue('checklist', updatedItems);
      setNewChecklistItem('');
    }
  };

  // Remove checklist item
  const removeChecklistItem = (index: number) => {
    const updatedItems = checklistItems.filter((_, i) => i !== index);
    setChecklistItems(updatedItems);
    setValue('checklist', updatedItems);
  };

  // Toggle checklist item
  const toggleChecklistItem = (index: number) => {
    const updatedItems = checklistItems.map((item, i) =>
      i === index ? { ...item, completed: !item.completed } : item
    );
    setChecklistItems(updatedItems);
    setValue('checklist', updatedItems);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 text-left">
            {isEditing ? 'Edit Task' : 'Create New Task'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Update task details and settings' : 'Add a new task to the system'}
          </p>
        </div>
        <button
          onClick={() => navigate('/tasks')}
          className="btn-secondary flex items-center space-x-2"
        >
          <XMarkIcon className="w-5 h-5" />
          <span>Cancel</span>
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 text-left mb-1">
                    Task Title *
                  </label>
                  <input
                    id="title"
                    type="text"
                    {...register('title', { required: 'Title is required' })}
                    className="input-field"
                    placeholder="Enter task title..."
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 text-left mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={4}
                    {...register('description')}
                    className="input-field"
                    placeholder="Describe the task details..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 text-left mb-1">
                      Task Type *
                    </label>
                    <div className="relative">
                      <select
                        id="type"
                        {...register('type', { required: 'Type is required' })}
                        className="input-field"
                        style={{ paddingLeft: '2rem' }}
                      >
                        <option value="Initial Contact">Initial Contact</option>
                        <option value="Assessment">Assessment</option>
                        <option value="Facility Visit">Facility Visit</option>
                        <option value="Documentation">Documentation</option>
                        <option value="Insurance Verification">Insurance Verification</option>
                        <option value="Contract Review">Contract Review</option>
                        <option value="Move Coordination">Move Coordination</option>
                        <option value="Follow-up">Follow-up</option>
                        <option value="Other">Other</option>
                      </select>
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <ClockIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700 text-left mb-1">
                      Estimated Hours
                    </label>
                    <input
                      id="estimatedHours"
                      type="number"
                      step="0.5"
                      min="0"
                      {...register('estimatedHours')}
                      className="input-field"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Client Selection (only for client-related tasks) */}
                {(watchedType === 'Initial Contact' || watchedType === 'Assessment' || watchedType === 'Facility Visit' || watchedType === 'Move Coordination') && (
                  <div>
                    <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 text-left mb-1">
                      Related Client
                    </label>
                    <div className="relative">
                      <select
                        id="clientId"
                        {...register('clientId')}
                        className="input-field"
                        style={{ paddingLeft: '2rem' }}
                      >
                        <option value="">Select a client...</option>
                        {clients.map((client) => (
                          <option key={client._id} value={client._id}>
                            {client.firstName} {client.lastName}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <UserIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Checklist */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Task Checklist</h3>
              
              <div className="space-y-4">
                {/* Add new checklist item */}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                    className="flex-1 input-field"
                    placeholder="Add checklist item..."
                  />
                  <button
                    type="button"
                    onClick={addChecklistItem}
                    className="btn-primary px-4"
                  >
                    Add
                  </button>
                </div>

                {/* Checklist items */}
                {checklistItems.length > 0 && (
                  <div className="space-y-2">
                    {checklistItems.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={() => toggleChecklistItem(index)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className={`flex-1 text-sm ${item.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {item.item}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeChecklistItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {checklistItems.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No checklist items yet. Add some tasks to break down this work.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Task Settings */}
            <div className="bg-white rounded-lg shadow-card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Task Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 text-left mb-1">
                    Priority *
                  </label>
                  <div className="relative">
                    <select
                      id="priority"
                      {...register('priority', { required: 'Priority is required' })}
                      className="input-field"
                      style={{ paddingLeft: '2rem' }}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Urgent">Urgent</option>
                    </select>
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <FlagIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  {errors.priority && (
                    <p className="mt-1 text-sm text-red-600">{errors.priority.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 text-left mb-1">
                    Status *
                  </label>
                  <select
                    id="status"
                    {...register('status', { required: 'Status is required' })}
                    className="input-field"
                  >
                    <option value="New Client">New Client</option>
                    <option value="Active Client">Active Client</option>
                    <option value="Target">Target</option>
                    <option value="Placed">Placed</option>
                    <option value="Follow-up">Follow-up</option>
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 text-left mb-1">
                    Due Date *
                  </label>
                  <div className="relative">
                    <input
                      id="dueDate"
                      type="date"
                      {...register('dueDate', { required: 'Due date is required' })}
                      className="input-field"
                      style={{ paddingLeft: '2rem' }}
                    />
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  {errors.dueDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.dueDate.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 text-left mb-1">
                    Assigned To *
                  </label>
                  <div className="relative">
                    <select
                      id="assignedTo"
                      {...register('assignedTo', { required: 'Assignee is required' })}
                      className="input-field"
                      style={{ paddingLeft: '2rem' }}
                    >
                      <option value="">Select assignee...</option>
                      {users.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.firstName} {user.lastName}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <UserIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                  {errors.assignedTo && (
                    <p className="mt-1 text-sm text-red-600">{errors.assignedTo.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-bold text-blue-900 mb-4 text-center">Task Types</h4>
              <ul className="text-sm text-blue-800 space-y-2 text-left">
                <li><strong>Initial Contact:</strong> First client interactions</li>
                <li><strong>Assessment:</strong> Client needs evaluation</li>
                <li><strong>Facility Visit:</strong> Tours and evaluations</li>
                <li><strong>Documentation:</strong> Paperwork and records</li>
                <li><strong>Move Coordination:</strong> Moving assistance</li>
                <li><strong>Follow-up:</strong> Ongoing client support</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/tasks')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" color="white" />
                <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              <>
                <PencilIcon className="w-5 h-5" />
                <span>{isEditing ? 'Update Task' : 'Create Task'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskForm;
